import { useMemo, useState } from 'react';
import { line, sortRows, t, viewerScopeKey, type ColumnDef, type MergedListAnswer, type MergedRow, type PageSize, type SortState } from '@bms2/shared';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer, type ListView } from '../../components/composites/ListViewer';
import { useApi } from '../records/api';
import styles from './DuplicatesPage.module.css';
import { DUPLICATES_MERGES_PATH } from './UndoMergeWindow';

/** 🔒 The Undo Merge cell's own address: a link cell, so it reads as a press; the page opens the window rather than leaving. */
export const UNDO_ROUTE_PREFIX = '/companies/duplicates/undo/';

interface MergedLine {
  key: string;
  mergedAway: string;
  kind: string;
  into: string;
  intoRoute: string | null;
  mergedAt: string;
  mergedBy: string;
  until: string;
  action: string;
  undoRoute: string | null;
}

const COLUMNS: readonly ColumnDef[] = [
  { key: 'mergedAway', label: t('duplicates.merged.col.mergedAway'), role: 'primary', type: 'text' },
  // 🔒 Decision 7880: the kind carries a join's note, so it shares the row's width rather than fitting its text and squeezing Merged into.
  { key: 'kind', label: t('duplicates.merged.col.kind'), role: 'secondary', type: 'text' },
  { key: 'into', label: t('duplicates.merged.col.into'), role: 'secondary', type: 'link', linkPattern: ':intoRoute' },
  { key: 'mergedAt', label: t('duplicates.merged.col.on'), role: 'secondary', type: 'instant', fit: true },
  { key: 'mergedBy', label: t('duplicates.merged.col.by'), role: 'secondary', type: 'text', fit: true },
  { key: 'until', label: t('duplicates.merged.col.until'), role: 'secondary', type: 'instant', fit: true },
  { key: 'action', label: t('duplicates.col.action'), role: 'secondary', type: 'link', linkPattern: ':undoRoute', fit: true },
];

/** The kind word per record type: a string key is dotted lower camel case, so the type name is mapped, never spliced in. */
const MERGED_KIND_KEY = {
  CompanyRecord: 'duplicates.merged.kind.company',
  PersonRecord: 'duplicates.merged.kind.person',
  Item: 'duplicates.merged.kind.item',
} as const;

function lineOf(row: MergedRow): MergedLine {
  return {
    key: row.id,
    mergedAway: row.mergedName,
    // 🔒 Decision 7550: a Zoho contact the import's Merge joined reads as one, by the import.
    // 🔒 Decision 7880: a join to a company the same run created says so after its kind.
    kind: [row.byImport ? t(row.recordType === 'Item' ? 'duplicates.merged.kind.importItem' : 'duplicates.merged.kind.import') : t(MERGED_KIND_KEY[row.recordType]), row.joinNote].filter(Boolean).join(': '),
    into: row.intoName,
    intoRoute: row.intoRoute,
    mergedAt: row.mergedAt,
    mergedBy: row.mergedBy,
    until: row.until,
    action: row.open ? t('undoMerge.press') : t('duplicates.merged.ended'),
    undoRoute: row.open ? `${UNDO_ROUTE_PREFIX}${encodeURIComponent(row.id)}` : null,
  };
}

const readLine = (one: MergedLine, column: string): unknown => (one as unknown as Record<string, unknown>)[column] ?? '';
const idOfLine = (one: MergedLine): string => one.key;

/**
 * 🔒 The Duplicates report's Merged view (decision 7500; U2b): every merge not
 * undone, newest first, with the record merged away, the one it went into, the
 * day, who merged it and the last day it can be undone. A row within its ninety
 * days carries Undo Merge; one past them says the ninety days ended.
 */
export function MergedView({ apiBaseUrl, views, onViewChange, onRoute }: {
  apiBaseUrl: string;
  views: readonly ListView[];
  onViewChange: (id: string) => void;
  /** A link cell's press: the Undo Merge cell opens the window, any other address is followed. */
  onRoute: (route: string) => void;
}) {
  const fetched = useApi<MergedListAnswer>(DUPLICATES_MERGES_PATH, apiBaseUrl);
  const [sort, setSort] = useState<SortState | null>(null);
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const answer = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const lines = useMemo(() => sortRows((answer?.merges ?? []).map(lineOf), sort, readLine, idOfLine), [answer, sort]);
  return (
    <>
      <p className={styles.hint} data-merged-open={answer?.openCount ?? 0}>{line(answer?.openCount ?? 0, t('duplicates.merged.count'))}</p>
      <AsyncStatePanel state={fetched.state} emptyAction={t('duplicates.merged.empty')} onRetry={fetched.reload}>
        {() => (
          <div data-duplicates-view="merged" data-merged-rows={lines.length}>
            <ListViewer<MergedLine>
              viewStore={{ scopeKey: viewerScopeKey('duplicates'), apiBaseUrl }}
              recordType={t('duplicates.title')}
              views={views}
              currentViewId="merged"
              onViewChange={onViewChange}
              columns={COLUMNS}
              rows={lines}
              idOf={idOfLine}
              read={readLine}
              sort={sort}
              onSortChange={setSort}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              emptyAction={t('duplicates.merged.empty')}
              selectable={false}
              onRoute={onRoute}
            />
          </div>
        )}
      </AsyncStatePanel>
    </>
  );
}
